import { useState, useMemo, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useMetricTrend, type MetricTrendGroupBy, type MetricTrendGranularity, type MetricTrendComparison } from "@/hooks/use-metrics";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "@/components/capacity/interactive-legend";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { formatTrendDate, COMPARISON_OPTIONS as SHARED_COMPARISON_OPTIONS } from "@/lib/trend-comparison";
import { useGlossaryLookup } from "@/hooks/use-glossary";
import { buildChartNarrative, type ConditionLeaf, type FieldOperator } from "@/lib/metric-description";

const TREND_COLORS = [
  "hsl(var(--primary))",
  "hsl(142 70% 45%)",
  "hsl(38 92% 50%)",
  "hsl(280 65% 60%)",
  "hsl(0 72% 51%)",
  "hsl(210 70% 50%)",
  "hsl(170 60% 45%)",
  "hsl(320 65% 55%)",
];

const KENNEL_STATUS_LABELS: Record<string, string> = {
  available: "Available",
  unavail: "Unavailable",
  evaluation: "Evaluation",
  strayWait: "Stray Wait",
  unknown: "Unknown",
};

const DAY_OPTIONS = [7, 30, 90] as const;

const ALL_GROUPBY_OPTIONS: { value: MetricTrendGroupBy; label: string; conditionField?: string }[] = [
  { value: "none",           label: "Total only" },
  { value: "species",        label: "By species",        conditionField: "species" },
  { value: "location",       label: "By location",       conditionField: "location" },
  { value: "kennel_status",  label: "By kennel status",  conditionField: "kennel_status" },
  { value: "adoption_level", label: "By adoption level", conditionField: "adoption_level" },
  { value: "zone",           label: "By kennel",         conditionField: "zone_code" },
];

const COMPARISON_MIN_DAYS: Record<string, number> = { wow: 7, mom: 30 };

interface LooseConditionLeaf {
  field: string;
  operator?: string;
  value?: unknown;
}

interface MetricConditionTree {
  conditions: LooseConditionLeaf[];
}

interface MetricChartViewProps {
  metricId: string;
  locationOverride?: string;
  conditionFields?: string[];
  conditions?: MetricConditionTree;
}

function DeltaBadge({ current, previous }: { current: number; previous: number | null }) {
  if (previous === null) return null;
  const delta = current - previous;
  if (delta === 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" /> no change
      </span>
    );
  }
  const isUp = delta > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${isUp ? "text-destructive" : "text-emerald-600 dark:text-emerald-500"}`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isUp ? "+" : ""}{delta} vs prior period
    </span>
  );
}

function resolveSeriesLabel(key: string, label: string, groupBy: MetricTrendGroupBy): string {
  if (groupBy === "kennel_status") {
    const rawKey = key.startsWith("kennel_status:") ? key.slice("kennel_status:".length) : key;
    return KENNEL_STATUS_LABELS[rawKey] ?? label;
  }
  return label;
}

export function MetricChartView({ metricId, locationOverride, conditionFields, conditions }: MetricChartViewProps) {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [granularity, setGranularity] = useState<MetricTrendGranularity>("daily");
  const [groupBy, setGroupBy] = useState<MetricTrendGroupBy>("none");
  const [comparisonMode, setComparisonMode] = useState<MetricTrendComparison>("none");

  const fixedFields = useMemo(() => new Set(conditionFields ?? []), [conditionFields]);
  const availableGroupByOptions = useMemo(
    () => ALL_GROUPBY_OPTIONS.filter(
      (o) => !o.conditionField || !fixedFields.has(o.conditionField)
    ),
    [fixedFields],
  );

  const availableComparisonOptions = useMemo(
    () => SHARED_COMPARISON_OPTIONS.filter(
      (o) => o.value === "none" || days >= (COMPARISON_MIN_DAYS[o.value] ?? 0)
    ),
    [days],
  );

  useEffect(() => {
    const available = availableGroupByOptions.map((o) => o.value);
    if (!available.includes(groupBy)) setGroupBy("none");
  }, [availableGroupByOptions, groupBy]);

  useEffect(() => {
    const available = availableComparisonOptions.map((o) => o.value);
    if (!available.includes(comparisonMode)) setComparisonMode("none");
  }, [availableComparisonOptions, comparisonMode]);

  const { data, isLoading, isError, refetch } = useMetricTrend(
    metricId,
    { days, granularity, groupBy, comparisonMode, locationOverride },
  );

  const glossaryLookup = useGlossaryLookup();

  const narrative = useMemo(() => {
    const leaves = (conditions?.conditions ?? [])
      .filter((c): c is ConditionLeaf =>
        typeof c.operator === "string" && c.operator.length > 0
      )
      .map((c) => ({ ...c, operator: c.operator as FieldOperator }));
    return buildChartNarrative(leaves, glossaryLookup, days, granularity, groupBy);
  }, [conditions, glossaryLookup, days, granularity, groupBy]);

  const legendSeries: LegendSeries[] = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((s, i) => ({
      key: s.key,
      label: resolveSeriesLabel(s.key, s.label, groupBy),
      color: TREND_COLORS[i % TREND_COLORS.length],
    }));
  }, [data, groupBy]);

  const { visibleSeries, toggleSeries } = useToggleableSeries(legendSeries);

  const chartData = useMemo(() => {
    if (!data || data.dates.length === 0) return [];
    return data.dates.map((date, i) => {
      const point: Record<string, string | number> = {
        date,
        formattedDate: formatTrendDate(date),
      };
      for (const s of data.series) {
        point[s.key] = s.data[i] ?? 0;
      }
      return point;
    });
  }, [data]);

  const tickInterval = useResponsiveTickInterval(chartData.length);
  const hasData = chartData.length > 0;

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {DAY_OPTIONS.map((d) => (
            <Button
              key={d}
              type="button"
              variant={days === d ? "default" : "outline"}
              size="sm"
              onClick={() => setDays(d)}
              data-testid={`button-trend-days-${d}-${metricId}`}
            >
              {d}d
            </Button>
          ))}
        </div>
        <Select value={granularity} onValueChange={(v) => setGranularity(v as MetricTrendGranularity)}>
          <SelectTrigger className="h-8 w-[100px] text-xs" data-testid={`select-granularity-${metricId}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupBy} onValueChange={(v) => setGroupBy(v as MetricTrendGroupBy)}>
          <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`select-groupby-${metricId}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableGroupByOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={comparisonMode} onValueChange={(v) => setComparisonMode(v as MetricTrendComparison)}>
          <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`select-comparison-${metricId}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableComparisonOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Smart narrative description */}
      <p className="text-xs text-muted-foreground leading-relaxed" data-testid={`text-chart-narrative-${metricId}`}>
        <span className="font-medium text-foreground">Showing:</span> {narrative}
      </p>

      {/* StatCards */}
      {!isLoading && hasData && (
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">As of last report</span>
            <span className="text-xl font-bold tabular-nums" data-testid={`stat-current-${metricId}`}>
              {data!.currentCount}
            </span>
          </div>
          {comparisonMode !== "none" && data!.previousCount !== null && (
            <>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Prior period</span>
                <span className="text-xl font-bold tabular-nums text-muted-foreground" data-testid={`stat-previous-${metricId}`}>
                  {data!.previousCount}
                </span>
              </div>
              <DeltaBadge current={data!.currentCount} previous={data!.previousCount} />
            </>
          )}
        </div>
      )}

      {/* Degradation banner — shown above chart when conditions can't be applied to historical data */}
      {!isLoading && data?.hasNonSnapshotableConditions && (
        <div className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/20 px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-500" />
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Some conditions cannot be applied to historical data — trend data may be incomplete.
          </p>
        </div>
      )}

      {/* Loading */}
      {isLoading && <Skeleton className="h-[240px] w-full" />}

      {/* Error */}
      {!isLoading && isError && (
        <div className="flex flex-col items-center justify-center h-[240px] gap-3 text-sm text-muted-foreground">
          <p>Failed to load trend data.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid={`button-retry-trend-${metricId}`}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && !hasData && (
        <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground text-center px-4" data-testid={`empty-trend-${metricId}`}>
          Trend data will appear after the first daily snapshot runs.
        </div>
      )}

      {/* Chart */}
      {!isLoading && !isError && hasData && (
        <>
          {legendSeries.length > 1 && (
            <InteractiveLegend
              series={legendSeries}
              visibleSeries={visibleSeries}
              onToggle={toggleSeries}
              compact
              testIdPrefix={`metric-chart-legend-${metricId}`}
            />
          )}
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <defs>
                {legendSeries.map((s) => (
                  <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="formattedDate"
                tick={{ fontSize: 11 }}
                className="text-muted-foreground"
                interval={tickInterval}
              />
              <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  borderColor: "hsl(var(--border))",
                  borderRadius: "6px",
                  color: "hsl(var(--card-foreground))",
                  fontSize: 12,
                }}
              />
              {legendSeries
                .filter((s) => visibleSeries.has(s.key))
                .map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    fill={`url(#grad-${s.key})`}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
